import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Navbar, Nav, Button } from 'react-bootstrap';
import {
  FaFileContract,
  FaUserCheck,
  FaCreditCard,
  FaTimesCircle as FaNoRefund,
  FaExclamationTriangle,
  FaShieldAlt,
  FaCopyright,
  FaBalanceScale,
  FaEnvelope,
  FaCalendarAlt,
  FaArrowRight,
  FaHandshake,
  FaBan,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaTimesCircle,
} from 'react-icons/fa';

const TermsOfService = () => {
  const [openSection, setOpenSection] = useState(0);

  useEffect(() => {
    // ✅ Page Title
    document.title = 'Terms of Service | Meetify';

    // ✅ Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Read Meetify Terms of Service. Understand your rights, subscription plans, refund policy, and acceptable use guidelines for using our meeting scheduler.');

    // ✅ Meta Robots
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) metaRobots.setAttribute('content', 'index, follow');

    // ✅ Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://meetify.co.in/terms-of-service';

    // ✅ OG Tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Terms of Service | Meetify');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', 'Meetify Terms of Service — subscription plans, refund policy, acceptable use, and more.');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://meetify.co.in/terms-of-service');

    // Cleanup on unmount — restore defaults
    return () => {
      document.title = 'Meetify — Smart Meeting Scheduler | Book Meetings Instantly';
      if (metaDesc) metaDesc.setAttribute('content', 'Meetify is a smart meeting scheduling platform. Share your availability, let others book meetings, and sync with Google Calendar — all in one place.');
      if (canonical) canonical.href = 'https://meetify.co.in/';
    };
  }, []);

  const toggleSection = (index) => {
    setOpenSection(openSection === index ? null : index);
  };

  const termsSections = [
    {
      title: '1. Acceptance of Terms',
      icon: <FaHandshake className="text-primary" />,
      content: [
        'By accessing or using Meetify ("the Service"), you confirm that you have read, understood, and agreed to be bound by these Terms of Service ("Terms").',
        'If you are using Meetify on behalf of a company or organization, you represent that you have the authority to bind that entity to these Terms.',
        'If you do not agree to these Terms, please do not access or use the Service.',
      ],
    },
    {
      title: '2. Description of Service',
      icon: <FaFileContract className="text-info" />,
      content: [
        'Meetify is a cloud-based meeting scheduling and management platform that enables individuals and businesses to:',
        '• Schedule and manage one-on-one and group meetings',
        '• Share availability via personalized booking links',
        '• Integrate with external calendars (e.g., Google Calendar)',
        '• Automate meeting reminders and follow-ups',
        '• Conduct meetings via integrated video conferencing tools',
        'We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time with reasonable notice.',
      ],
    },
    {
      title: '3. Eligibility & Account Registration',
      icon: <FaUserCheck className="text-success" />,
      content: [
        'To use Meetify, you must:',
        '• Be at least 18 years of age',
        '• Possess the legal authority to enter into these Terms',
        '• Provide accurate, complete, and up-to-date registration information',
        'You are solely responsible for maintaining the confidentiality of your account credentials. Meetify is not liable for any loss or damage arising from unauthorized access to your account.',
        'You must notify us immediately at meetify.co.in@gmail.com if you suspect any unauthorized use of your account.',
      ],
    },
    {
      title: '4. Subscription Plans & Payments',
      icon: <FaCreditCard className="text-warning" />,
      content: [
        'Meetify is offered under a subscription model with the following plan tiers:',
        '• Free Plan: Limited features, up to 5 scheduled events per month',
        '• Pro Plan (Monthly/Yearly): Full access to all scheduling features, calendar integrations, and analytics',
        '• Enterprise Plan: Custom pricing, dedicated support, and advanced admin controls',
        'All payments are processed in Indian Rupees (INR) unless otherwise stated. Applicable taxes (e.g., 18% GST) will be added at checkout.',
        'Subscription fees are billed in advance. Failure to pay may result in suspension of your account until dues are cleared.',
      ],
    },
    {
      title: '5. Refund & Cancellation Policy',
      icon: <FaTimesCircle className="text-danger" />,
      content: [
        'All subscription fees paid to Meetify are strictly non-refundable.',
        '• No refunds will be issued for any reason, including but not limited to: unused subscription days, accidental purchases, or dissatisfaction with the Service.',
        '• Cancellation of your subscription will stop future billing but will not entitle you to a refund of any amount already paid.',
        '• Free Plan users are not charged and therefore no refunds are applicable.',
        'By subscribing to Meetify, you explicitly acknowledge and agree to this No-Refund Policy. We encourage you to evaluate our Free Plan before upgrading to a paid subscription.',
      ],
    },
    {
      title: '6. Acceptable Use Policy',
      icon: <FaBan className="text-danger" />,
      content: [
        'You agree not to use Meetify to:',
        '• Send spam, unsolicited messages, or phishing communications through meeting invites',
        '• Impersonate any person or organization in booking links or meeting profiles',
        '• Collect data from other users without their explicit consent',
        '• Disrupt, overload, or attempt to hack the platform or its infrastructure',
        '• Engage in any activity that violates applicable laws or regulations',
        'Violations of this policy may result in immediate suspension or permanent termination of your account without refund.',
      ],
    },
    {
      title: '7. Calendar & Third-Party Integrations',
      icon: <FaCalendarAlt className="text-primary" />,
      content: [
        'Meetify integrates with third-party services such as Google Calendar, Zoom, and others. By enabling these integrations, you authorize Meetify to access and manage relevant data from those platforms.',
        'Meetify does not store your third-party credentials. However, we do store access tokens required to maintain integrations.',
        'We are not responsible for the availability, accuracy, or security of third-party services. Use of these services is subject to their respective terms and privacy policies.',
      ],
    },
    {
      title: '8. Data Privacy & Security',
      icon: <FaShieldAlt className="text-success" />,
      content: [
        'Your privacy is our priority. We collect and process only the data necessary to provide and improve the Service.',
        'Meetify implements industry-standard security measures including SSL/TLS encryption, secure cloud infrastructure, and role-based access controls.',
        'Meeting data (titles, participants, times) is stored securely and is never shared with third parties for advertising purposes.',
        'Please refer to our Privacy Policy for a complete explanation of how we collect, use, and protect your personal information.',
      ],
    },
    {
      title: '9. Intellectual Property',
      icon: <FaCopyright className="text-secondary" />,
      content: [
        'All content, trademarks, logos, software, and design elements within Meetify are the exclusive intellectual property of Meetify and its licensors.',
        'You may not copy, modify, distribute, sell, or lease any part of the Service without prior written consent.',
        'You retain ownership of the content you create through Meetify (e.g., meeting notes, event descriptions). By posting such content, you grant Meetify a limited license to use it solely for providing the Service.',
      ],
    },
    {
      title: '10. Limitation of Liability',
      icon: <FaBalanceScale className="text-warning" />,
      content: [
        'To the maximum extent permitted by applicable law, Meetify shall not be liable for:',
        '• Loss of profits, data, or business opportunities',
        '• Service interruptions or technical failures beyond our control',
        '• Actions of third-party services integrated with Meetify',
        '• Indirect, incidental, punitive, or consequential damages of any kind',
        'Our total cumulative liability to you for any claims arising from your use of the Service shall not exceed the amount you paid for the Service in the 3 months preceding the claim.',
      ],
    },
    {
      title: '11. Disclaimer of Warranties',
      icon: <FaExclamationTriangle className="text-danger" />,
      content: [
        'Meetify is provided on an "as is" and "as available" basis without warranties of any kind, either express or implied.',
        'We do not warrant that:',
        '• The Service will be uninterrupted, error-free, or completely secure',
        '• The results obtained from the Service will meet your expectations',
        '• All bugs, errors, or defects will be corrected in a timely manner',
        'Some jurisdictions do not allow certain warranty exclusions, so some of the above may not apply to you.',
      ],
    },
    {
      title: '12. Termination',
      icon: <FaTimesCircle className="text-secondary" />,
      content: [
        'Either party may terminate this agreement at any time:',
        '• You may cancel your account through the Account Settings page.',
        '• Meetify may suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or fail to pay subscription fees.',
        'Upon termination, your access to the Service will stop immediately. We may retain your data for up to 30 days post-termination before permanent deletion, unless legal obligations require longer retention.',
      ],
    },
    {
      title: '13. Changes to These Terms',
      icon: <FaSyncAlt className="text-info" />,
      content: [
        'We may update these Terms of Service from time to time. When we do, we will:',
        '• Post the revised Terms on this page with an updated "Last Updated" date',
        '• Notify active users via email at least 7 days before significant changes take effect',
        'Your continued use of Meetify after the effective date of any changes constitutes acceptance of the updated Terms.',
      ],
    },
    {
      title: '14. Governing Law',
      icon: <FaBalanceScale className="text-primary" />,
      content: [
        'These Terms of Service are governed by and construed in accordance with the laws of India.',
        'Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts located in India.',
        'We encourage resolving disputes informally first by contacting our support team at meetify.co.in@gmail.com before pursuing any legal action.',
      ],
    },
  ];

  const highlights = [
    {
      icon: <FaCheckCircle size={28} className="text-success" />,
      title: 'Free Plan Available',
      desc: 'Get started with 5 events/month at no cost',
    },
    {
      icon: <FaBan size={28} className="text-danger" />,
      title: 'No Refund Policy',
      desc: 'All payments are final and non-refundable',
    },
    {
      icon: <FaShieldAlt size={28} className="text-primary" />,
      title: 'Your Data is Secure',
      desc: 'SSL encryption & secure cloud storage',
    },
    {
      icon: <FaUserCheck size={28} className="text-info" />,
      title: 'No Lock-in',
      desc: 'Cancel your subscription at any time',
    },
  ];

  return (
    <div className="tos-page">
      {/* Navbar */}
      <Navbar expand="lg" className="tos-navbar py-3 shadow-sm">
        <Container>
          <Navbar.Brand className="fw-bold fs-3 brand-logo" href="/">
            <FaCalendarAlt className="me-2" />
            Meetify
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="tos-navbar-nav" />
          <Navbar.Collapse id="tos-navbar-nav">
            <Nav className="ms-auto align-items-center">
              <Nav.Link href="/#features" className="nav-link-custom mx-2">Features</Nav.Link>
              <Nav.Link href="/#pricing" className="nav-link-custom mx-2">Pricing</Nav.Link>
              <Nav.Link href="/PrivacyPolicy" className="nav-link-custom mx-2">Privacy</Nav.Link>
              <Button variant="primary" size="sm" className="ms-3 px-4 tos-btn" href="/login">
                Sign In
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero Header */}
      <div className="tos-hero">
        <Container>
          <Row className="justify-content-center text-center py-5">
            <Col lg={8}>
              <div className="hero-icon-wrap mx-auto mb-4">
                <FaFileContract size={44} className="text-white" />
              </div>
              <h1 className="tos-hero-title mb-3">Terms of Service</h1>
              <p className="tos-hero-subtitle mb-4">
                These Terms govern your use of Meetify — our smart meeting scheduling platform.
                Please read them carefully before using our Service.
              </p>
              <Badge className="tos-badge">
                Last Updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              </Badge>
            </Col>
          </Row>
        </Container>
        <div className="tos-hero-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f8fafc" />
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="tos-body">
        <Container className="py-5">

          {/* Highlights */}
          <Row className="mb-5 g-4">
            {highlights.map((item, i) => (
              <Col key={i} xs={12} sm={6} lg={3}>
                <Card className="tos-highlight-card h-100 border-0 text-center shadow-sm">
                  <Card.Body className="p-4">
                    <div className="highlight-icon mb-3">{item.icon}</div>
                    <h6 className="fw-bold text-dark mb-1">{item.title}</h6>
                    <p className="text-muted small mb-0">{item.desc}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Notice Banner */}
          <Row className="mb-5">
            <Col lg={10} className="mx-auto">
              <div className="notice-banner d-flex align-items-start gap-3 p-4 rounded-3">
                <FaExclamationTriangle size={22} className="text-warning flex-shrink-0 mt-1" />
                <div>
                  <strong className="d-block mb-1" style={{ color: '#92400e' }}>Important Notice</strong>
                  <p className="mb-0" style={{ color: '#78350f', fontSize: '0.95rem' }}>
                    By using Meetify, you acknowledge that you have read, understood, and agree to be
                    bound by these Terms of Service. If you do not agree, please discontinue use of the Service.
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Terms Accordion */}
          <Row>
            <Col lg={10} className="mx-auto">
              <div className="tos-accordion">
                {termsSections.map((section, index) => (
                  <div key={index} className={`tos-section ${openSection === index ? 'active' : ''}`}>
                    <button
                      className="tos-section-header w-100 d-flex align-items-center justify-content-between"
                      onClick={() => toggleSection(index)}
                      id={`tos-section-${index}`}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <span className="section-icon">{section.icon}</span>
                        <span className="section-title">{section.title}</span>
                      </div>
                      <span className="toggle-icon">
                        {openSection === index ? <FaChevronUp /> : <FaChevronDown />}
                      </span>
                    </button>
                    {openSection === index && (
                      <div className="tos-section-body">
                        {section.content.map((line, i) => (
                          <p
                            key={i}
                            className={`mb-2 ${line.startsWith('•') ? 'bullet-line' : ''}`}
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          {/* Contact Section */}
          <Row className="mt-5">
            <Col lg={8} className="mx-auto">
              <Card className="tos-contact-card border-0 shadow-sm text-center">
                <Card.Body className="p-5">
                  <div className="contact-icon-wrap mx-auto mb-4">
                    <FaEnvelope size={32} className="text-white" />
                  </div>
                  <h3 className="fw-bold text-dark mb-2">Have Questions?</h3>
                  <p className="text-muted mb-4">
                    If you have any questions or concerns about these Terms of Service, our team is here to help.
                  </p>
                  <div className="d-flex flex-column flex-md-row justify-content-center gap-4 flex-wrap">
                    <div className="contact-item">
                      <div className="fw-semibold text-dark mb-1">Rishabh Mandal</div>
                      <a href="mailto:Mandalrishabh60@yahoo.com" className="text-decoration-none contact-email">
                        Mandalrishabh60@yahoo.com
                      </a>
                    </div>
                    <div className="contact-divider d-none d-md-block" />
                    <div className="contact-item">
                      <div className="fw-semibold text-dark mb-1">Nitesh Mandal</div>
                      <a href="mailto:Niteshmandal007@yahoo.com" className="text-decoration-none contact-email">
                        Niteshmandal007@yahoo.com
                      </a>
                    </div>
                    <div className="contact-divider d-none d-md-block" />
                    <div className="contact-item">
                      <div className="fw-semibold text-dark mb-1">General Support</div>
                      <a href="mailto:meetify.co.in@gmail.com" className="text-decoration-none contact-email">
                        meetify.co.in@gmail.com
                      </a>
                    </div>
                  </div>
                  <Button variant="primary" className="mt-4 px-4 tos-btn" href="mailto:meetify.co.in@gmail.com">
                    <FaEnvelope className="me-2" />
                    Contact Support
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

        </Container>
      </div>

      {/* Footer */}
      <footer className="tos-footer py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={5}>
              <div className="d-flex align-items-center mb-2">
                <FaCalendarAlt className="me-2 text-primary" />
                <span className="fw-bold text-white">Meetify</span>
              </div>
              <p className="footer-copy mb-0">
                © {new Date().getFullYear()} Meetify. All rights reserved.
              </p>
            </Col>
            <Col md={7} className="text-md-end mt-3 mt-md-0">
              <a href="/PrivacyPolicy" className="footer-link me-4">Privacy Policy</a>
              <a href="/TermsAndConditions" className="footer-link me-4">Terms & Conditions</a>
              <a href="/terms-of-service" className="footer-link me-4 footer-link-active">Terms of Service</a>
              <a href="/helpcenter" className="footer-link">Contact</a>
            </Col>
          </Row>
        </Container>
      </footer>

      <style>{`
        /* ===== FONTS ===== */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .tos-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: #f8fafc;
          min-height: 100vh;
        }

        /* ===== NAVBAR ===== */
        .tos-navbar {
          background: #ffffff !important;
          border-bottom: 1px solid #e2e8f0;
        }

        .brand-logo {
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .nav-link-custom {
          color: #4a5568 !important;
          font-weight: 500;
          transition: color 0.2s;
        }

        .nav-link-custom:hover {
          color: #667eea !important;
        }

        .tos-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          border: none !important;
          border-radius: 8px !important;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .tos-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.35);
        }

        /* ===== HERO ===== */
        .tos-hero {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          padding-bottom: 0;
        }

        .hero-icon-wrap {
          width: 90px;
          height: 90px;
          background: rgba(255,255,255,0.2);
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.3);
        }

        .tos-hero-title {
          font-size: 2.8rem;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.5px;
        }

        .tos-hero-subtitle {
          font-size: 1.1rem;
          color: rgba(255,255,255,0.85);
          max-width: 600px;
          margin: 0 auto;
        }

        .tos-badge {
          background: rgba(255,255,255,0.2) !important;
          color: #ffffff !important;
          font-size: 0.85rem;
          font-weight: 500;
          padding: 6px 16px;
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.3);
        }

        .tos-hero-wave {
          line-height: 0;
        }

        .tos-hero-wave svg {
          width: 100%;
          height: 80px;
          display: block;
        }

        /* ===== BODY ===== */
        .tos-body {
          background: #f8fafc;
        }

        /* ===== HIGHLIGHT CARDS ===== */
        .tos-highlight-card {
          border-radius: 16px !important;
          transition: all 0.3s ease;
        }

        .tos-highlight-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1) !important;
        }

        .highlight-icon {
          width: 60px;
          height: 60px;
          background: #f1f5f9;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }

        /* ===== NOTICE BANNER ===== */
        .notice-banner {
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-left: 4px solid #f59e0b;
        }

        /* ===== ACCORDION ===== */
        .tos-accordion {
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 24px rgba(0,0,0,0.07);
          background: #fff;
        }

        .tos-section {
          border-bottom: 1px solid #e2e8f0;
          transition: background 0.2s;
        }

        .tos-section:last-child {
          border-bottom: none;
        }

        .tos-section-header {
          background: transparent;
          border: none;
          padding: 20px 24px;
          cursor: pointer;
          text-align: left;
          transition: background 0.2s;
        }

        .tos-section-header:hover {
          background: #f8fafc;
        }

        .tos-section.active .tos-section-header {
          background: linear-gradient(135deg, rgba(102,126,234,0.06), rgba(118,75,162,0.06));
          border-left: 4px solid #667eea;
        }

        .section-icon {
          font-size: 1.1rem;
        }

        .section-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
        }

        .toggle-icon {
          color: #94a3b8;
          font-size: 0.85rem;
        }

        .tos-section-body {
          padding: 4px 24px 24px 62px;
          animation: slideDown 0.25s ease;
        }

        .tos-section-body p {
          color: #475569;
          font-size: 0.95rem;
          line-height: 1.75;
          margin-bottom: 8px !important;
        }

        .bullet-line {
          padding-left: 8px;
          border-left: 2px solid #e2e8f0;
          color: #64748b !important;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ===== CONTACT CARD ===== */
        .tos-contact-card {
          border-radius: 20px !important;
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%) !important;
        }

        .contact-icon-wrap {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .contact-item {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .contact-divider {
          width: 1px;
          background: #e2e8f0;
          align-self: stretch;
        }

        .contact-email {
          color: #667eea;
          font-size: 0.88rem;
          transition: color 0.2s;
        }

        .contact-email:hover {
          color: #764ba2;
        }

        /* ===== FOOTER ===== */
        .tos-footer {
          background: #1e293b;
          border-top: 1px solid #334155;
        }

        .footer-copy {
          color: #94a3b8;
          font-size: 0.85rem;
        }

        .footer-link {
          color: #94a3b8;
          text-decoration: none;
          font-size: 0.9rem;
          transition: color 0.2s;
        }

        .footer-link:hover {
          color: #ffffff;
        }

        .footer-link-active {
          color: #a78bfa !important;
          font-weight: 600;
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 768px) {
          .tos-hero-title { font-size: 2rem; }
          .tos-hero-subtitle { font-size: 1rem; }
          .tos-section-body { padding-left: 20px; }
          .tos-section-header { padding: 16px 20px; }
          .footer-link { font-size: 0.8rem; margin-bottom: 4px; display: inline-block; }
        }

        @media (max-width: 576px) {
          .tos-hero-title { font-size: 1.7rem; }
          .hero-icon-wrap { width: 70px; height: 70px; border-radius: 18px; }
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;
